import React from 'react';
import TableHeading from '../../styled/TableHeading';
import HoldingsReallocatedStatement from '../../styled/HoldingsReallocatedStatement';
import MSCIStatement from '../../styled/MSCIStatement';
import { standardDateFormat } from '../../../utils/dateFormatter';
import { formatPercentage } from '../../../utils/numberFormatter';
import { MAX_PHONE_VIEW_ALLOCATION_BAR_LENGTH } from '../../../utils/constants';

import styles from './EquitiesChartTable.module.scss';

type Props = {
  className?: string;
  maxBarLength: number;
  selectedAccount: AssetAccount;
};

const EquitiesChartTable: React.FC<Props> = ({
  className,
  maxBarLength,
  selectedAccount
}) => {
  const getMaxBarLength = (
    val: number,
    maxVal: number,
    maxBarLength: number,
    percent: boolean
  ) => {
    return percent ? val + '%' : (val / maxVal) * maxBarLength + 'px';
  };

  const getBarChart = (
    sectorAllocations: SectorAllocation[],
    maxAllocation: number,
    maxBarLength: number
  ) => {
    return sectorAllocations.map((alloc, index) =>
      alloc.accountAllocation > 0 ? (
        <div key={index} className={styles['allocation']}>
          <div className={styles['allocation-label']}>{alloc.sectorName}</div>
          <div className={styles['allocation-values']}>
            <div className={styles['allocation-values']}>
              <div
                className={styles['t-rowe-allocation']}
                style={{
                  width: getMaxBarLength(
                    alloc.accountAllocation,
                    maxAllocation,
                    maxBarLength,
                    true
                  )
                }}
              />
              <div className={styles['percentage']}>
                {formatPercentage(alloc.accountAllocation, 1)}
              </div>
            </div>
            <div className={styles['allocation-values']}>
              <div
                className={styles['sp-500-allocation']}
                style={{
                  width: getMaxBarLength(
                    alloc.benchmarkAllocation.benchmarkValue,
                    maxAllocation,
                    maxBarLength,
                    true
                  ),
                  backgroundColor: 'solid #9d9d9d'
                }}
              />
              <div className={styles['percentage']}>
                {formatPercentage(alloc.benchmarkAllocation.benchmarkValue, 1)}
              </div>
            </div>
          </div>
        </div>
      ) : (
        ''
      )
    );
  };

  if (selectedAccount === undefined) {
    return null;
  }

  const {
    asOfDate,
    profile: { sectorAllocations }
  } = selectedAccount.monthlyReport;

  if (sectorAllocations.length === 0) {
    return null;
  }

  const maxAllocation = Math.max(
    ...sectorAllocations.map((alloc) => alloc.accountAllocation),
    ...sectorAllocations.map(
      (alloc) => alloc.benchmarkAllocation.benchmarkValue
    )
  );

  return (
    <div className={`${className} ${styles['equities-content-flex']}`}>
      <div className={styles['holdings-title']}>Portfolio Profile</div>
      <div className={styles['title-section']}>
        <span className={styles['table-title']}>Equities</span>
        <span className={styles['table-date']}>
          As of {standardDateFormat(asOfDate)}
        </span>
      </div>
      <div className={styles['charts']}>
        <TableHeading
          tableType="Sector Allocation As a Percentage of Total Equities"
          tableTypeClassName={styles['chart-title']}
        />
        <div className={styles['chart-legend']}>
          <div
            className={`${styles['t-rowe-portfolio']} ${styles['legend-label']}`}>
            Your T. Rowe Price Portfolio
          </div>
          <div
            className={`${styles['sp-500-portfolio']} ${styles['legend-label']}`}>
            S&amp;P 500 Index
          </div>
        </div>
        <div className={`${styles['chart']} ${styles['non-phone']}`}>
          {getBarChart(sectorAllocations, maxAllocation, maxBarLength)}
        </div>
        <div className={`${styles['chart']} ${styles['phone']}`}>
          {getBarChart(
            sectorAllocations,
            maxAllocation,
            MAX_PHONE_VIEW_ALLOCATION_BAR_LENGTH
          )}
        </div>
        <div className={styles['disclaimer']}>
          <HoldingsReallocatedStatement />
          <MSCIStatement />
        </div>
      </div>
    </div>
  );
};

export default EquitiesChartTable;
